// src/features/team/SkillPicker.tsx
import { CATEGORY_LABEL, CATEGORY_COLOR } from '../../core/constants';
import { useMemo, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import type { Skill } from '../../core/types';
import { SKILL_MAP } from '../../data/mock-skills';
import { MAX_SKILLS_PER_SLOT } from '../../store/useTeamStore';
import { pinyin } from 'pinyin-pro';
import { ATTRIBUTE_COLOR_MAP } from '../../data/attributes';

function getTypeStyle(type: string): string {
  const color = ATTRIBUTE_COLOR_MAP[type];
  if (!color) return 'bg-slate-500/20 text-slate-300 border-slate-500/40';
  // 用 inline style 更精确，但为了 class 统一，这里只借用颜色 token
  return 'border';
}



interface SkillPickerProps {
  open: boolean;
  onClose: () => void;
  /** 当前精灵可学的技能 ID 列表 */
  availableSkillIds: string[];
  /** 已选中的技能 ID 列表 */
  selectedSkillIds: string[];
  /** 切换选中状态 */
  onToggle: (skillId: string) => void;
  /** 精灵名称，用于标题展示 */
  spiritName?: string;
}

export function SkillPicker({
  open,
  onClose,
  availableSkillIds,
  selectedSkillIds,
  onToggle,
  spiritName,
}: SkillPickerProps) {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // 可用技能对象列表
  const availableSkills = useMemo(
    () =>
      availableSkillIds
        .map((id) => SKILL_MAP[id])
        .filter((s): s is Skill => Boolean(s)),
    [availableSkillIds],
  );

  // 所有属性
  const allTypes = useMemo(() => {
    const set = new Set<string>();
    availableSkills.forEach((s) => set.add(s.type));
    return Array.from(set);
  }, [availableSkills]);

  // 预生成拼音索引：skillId -> { full, first }
  const pinyinIndex = useMemo(() => {
    const map = new Map<string, { full: string; first: string }>();
    availableSkills.forEach((s) => {
      const full = pinyin(s.name, {
        toneType: 'none',
        type: 'array',
      })
        .join('')
        .toLowerCase();
      const first = pinyin(s.name, {
        pattern: 'first',
        toneType: 'none',
        type: 'array',
      })
        .join('')
        .toLowerCase();
      map.set(s.id, { full, first });
    });
    return map;
  }, [availableSkills]);

  // 过滤后的技能
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return availableSkills.filter((s) => {
      const matchType = !typeFilter || s.type === typeFilter;
      const matchCategory = !categoryFilter || s.category === categoryFilter;
      if (!matchType || !matchCategory) return false;
      if (!q) return true;

      // 1. 原始汉字 / id 匹配
      if (
        s.name.toLowerCase().includes(q) ||
        s.id.toLowerCase().includes(q)
      ) {
        return true;
      }

      // 2. 拼音全拼 / 首字母匹配
      const py = pinyinIndex.get(s.id);
      if (!py) return false;
      if (py.full.includes(q)) return true;
      if (py.first.includes(q)) return true;

      return false;
    });
  }, [availableSkills, query, typeFilter, categoryFilter, pinyinIndex]);

  if (!open) return null;

  const isFull = selectedSkillIds.length >= MAX_SKILLS_PER_SLOT;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white">选择技能</h2>
            {spiritName && (
              <p className="text-xs text-slate-400 mt-0.5">{spiritName}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-sm font-mono ${
                isFull ? 'text-yellow-400' : 'text-slate-400'
              }`}
            >
              {selectedSkillIds.length} / {MAX_SKILLS_PER_SLOT}
            </span>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition"
              aria-label="关闭"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* 搜索栏 */}
        <div className="px-6 py-3 border-b border-slate-700 space-y-3">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索技能名称 / 拼音 / 首字母 / ID..."
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-primary-500"
              autoFocus
            />
          </div>

          {/* 分类筛选 */}
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label="全部分类"
              active={categoryFilter === null}
              onClick={() => setCategoryFilter(null)}
            />
            {(['physical', 'special', 'status'] as const).map((c) => (
              <FilterChip
                key={c}
                label={CATEGORY_LABEL[c]}
                active={categoryFilter === c}
                onClick={() => setCategoryFilter(c)}
              />
            ))}
          </div>

          {/* 属性筛选 */}
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label="全部属性"
              active={typeFilter === null}
              onClick={() => setTypeFilter(null)}
            />
            {allTypes.map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`px-3 py-1 text-xs rounded-full border transition ${
                  typeFilter === t
                    ? 'bg-primary-600 text-white border-primary-500'
                    : getTypeStyle(t) + ' hover:opacity-80'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* 技能列表 */}
        <div className="flex-1 overflow-y-auto p-6">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-12">没有符合条件的技能</p>
          ) : (
            <div className="space-y-2">
              {filtered.map((skill) => {
                const selected = selectedSkillIds.includes(skill.id);
                const disabled = !selected && isFull;

                return (
                  <SkillRow
                    key={skill.id}
                    skill={skill}
                    selected={selected}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      onToggle(skill.id);
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* 底部提示 */}
        <div className="px-6 py-3 border-t border-slate-700 text-xs text-slate-400 flex justify-between items-center">
          <span>
            {isFull
              ? '技能已满，取消一个才能继续选择'
              : `还可选择 ${MAX_SKILLS_PER_SLOT - selectedSkillIds.length} 个技能`}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium transition"
          >
            完成
          </button>
        </div>
      </div>
    </div>
  );
}

/** 顶部筛选按钮 */
function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-xs rounded-full border transition ${
        active
          ? 'bg-primary-600 text-white border-primary-500'
          : 'bg-slate-700 text-slate-300 border-slate-600 hover:bg-slate-600'
      }`}
    >
      {label}
    </button>
  );
}

/** 单个技能行 */
function SkillRow({
  skill,
  selected,
  disabled,
  onClick,
}: {
  skill: Skill;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${
        selected
          ? 'border-primary-500 bg-primary-500/10'
          : disabled
            ? 'border-slate-800 bg-slate-900/30 opacity-40 cursor-not-allowed'
            : 'border-slate-700 bg-slate-900/50 hover:border-slate-500 hover:bg-slate-900'
      }`}
    >
      {/* 勾选框 */}
      <div
        className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center transition ${
          selected
            ? 'bg-primary-500 border-primary-500'
            : 'border-slate-600'
        }`}
      >
        {selected && <Check size={14} className="text-white" />}
      </div>

      {/* 技能信息 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-white text-sm truncate">
            {skill.name}
          </span>
          <span
            className={`shrink-0 px-2 py-0.5 text-[10px] rounded-full border ${getTypeStyle(skill.type)}`}
          >
            {skill.type}
          </span>
          <span
            className={`shrink-0 px-2 py-0.5 text-[10px] rounded-full border ${CATEGORY_COLOR[skill.category]}
            `}
            >
          {CATEGORY_LABEL[skill.category]}
        </span>
        </div>
        <div className="flex gap-4 text-xs text-slate-400">
          <span>
            威力 <span className="text-slate-200 font-mono">{skill.power}</span>
          </span>
          <span>
            命中 <span className="text-slate-200 font-mono">{skill.accuracy}</span>
          </span>
          {skill.priority ? (
            <span>
              优先度 <span className="text-slate-200 font-mono">+{skill.priority}</span>
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}